import { Box, Dialog, DialogContent, DialogProps, Typography, Icon } from '@mui/material';
import { FC, useState } from 'react';
import { InformationDialogTitle } from '../../../../lib/components/compound/dialogs/informationDialog/title';
import { CompanySettingsIcon, SelectEmployeeIcon } from '../../../../lib/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../lib/components/atomic/button';

type Props = {
  title?: string;
  confirmButtonText: string;
  cancelButtonText: string;
  onConfirm?: (assignmentMethod: string) => void;
  onClose?: () => void;
  onCancel?: () => void;
} & Pick<DialogProps, 'open'>;

export const AssignmentMethodSelectionDialog: FC<Props> = ({
  open,
  title,
  confirmButtonText,
  cancelButtonText,
  onConfirm,
  onClose,
  onCancel
}) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<string>('');

  const handleClick = (method: string) => {
    setSelectedMethod(method);
  };

  const handleConfirm = () => {
    onConfirm?.(selectedMethod);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        className: 'flex flex-col justify-center shadow-900 max-h-[52vh] w-[80%]'
      }}
    >
      {title && <InformationDialogTitle title={title} onClose={onClose} />}
      <DialogContent
        dividers
        sx={{
          borderBottom: 'none',
          paddingTop: '8px',
          paddingBottom: '8px',
          overflow: 'scroll'
        }}
      >
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Box
            onClick={() => handleClick('automatic')}
            sx={{
              marginTop: '28px',
              marginBottom: '10px',
              width: '100%',
              color: selectedMethod === 'automatic' ? 'primary.light' : 'grey.100',
              borderRadius: '15px',
              borderColor: selectedMethod === 'automatic' ? 'primary.light' : 'grey.100',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              fontFamily: 'Rubik',
              padding: '14px',
              position: 'relative',
              cursor: 'pointer',
              '&:hover': {
                borderColor: selectedMethod !== 'manual' ? 'primary.light' : 'grey.100',
                color: selectedMethod !== 'manual' ? 'primary.light' : 'grey.100'
              }
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                top: '-32px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '3px',
                borderRadius: '50%',
                zIndex: 1,
                fontSize: '40px'
              }}
            >
              <CompanySettingsIcon sx={{ fontSize: 'inherit' }} />
            </Icon>
            <Box
              sx={{
                color: selectedMethod !== 'manual' ? 'primary.light' : 'grey.100',
                '&:hover': {
                  color: 'primary.light'
                }
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '13px',
                  color: 'inherit'
                }}
              >
                {t('custom:automatic_assignment')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: 1.2,
                  color: 'inherit'
                }}
              >
                {t('custom:automatic_assignment_description')}
              </Typography>
            </Box>
          </Box>

          <Box
            onClick={() => handleClick('manual')}
            sx={{
              marginTop: '28px',
              marginBottom: '10px',
              width: '100%',
              color: selectedMethod === 'manual' ? 'primary.light' : 'grey.100',
              borderRadius: '15px',
              borderColor: selectedMethod === 'manual' ? 'primary.light' : 'grey.100',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              fontFamily: 'Rubik',
              padding: '14px',
              position: 'relative',
              cursor: 'pointer',
              '&:hover': {
                borderColor: selectedMethod !== 'automatic' ? 'primary.light' : 'grey.100',
                color: selectedMethod !== 'automatic' ? 'primary.light' : 'grey.100'
              }
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                top: '-32px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '3px',
                borderRadius: '50%',
                zIndex: 1,
                fontSize: '40px'
              }}
            >
              <SelectEmployeeIcon sx={{ fontSize: 'inherit' }} />
            </Icon>
            <Box
              sx={{
                color: selectedMethod !== 'automatic' ? 'primary.light' : 'grey.100',
                '&:hover': {
                  color: 'primary.light'
                }
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '13px',
                  color: 'inherit'
                }}
              >
                {t('custom:manual_assignment')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: 1.2,
                  color: 'inherit'
                }}
              >
                {t('custom:manual_assignment_description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            width: '100%',
            gap: '8px',
            marginTop: '16px',
            marginBottom: '16px'
          }}
        >
          <Button variant="secondary" onClick={handleCancel}>
            {cancelButtonText}
          </Button>
          <Button onClick={handleConfirm} disabled={selectedMethod === ''}>
            {confirmButtonText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
